import { History, Location, LocationDescriptor, createLocation } from "history";
import { parse } from "query-string";
import React, { useContext } from "react";
import { ReactReduxContext } from "react-redux";
import { LinkProps } from "react-router-dom";

import { loginUrl, signupUrl } from "@/mk2/apps/users/urls";
import { Route } from "@/mk2/containers/Application/Application";
import { hasRoute } from "@/mk2/helpers/router";
import { RouterContext } from "@/mk2/services/RouterContext";
import { apiAuth } from "@/mk2/services/browserXhr";
import { CURRENT_REVISION } from "@/mk/autogenerated/gitData";
import { hasAds } from "@/mk/bazaar/common/userUtils";
import { BASE_URL, IS_PROD } from "@/mk/settings";

// eslint-disable-line no-restricted-imports

export type OwnProps = LinkProps; // inherit from react-router-dom/LinkProps

interface InjectedProps {
    routes: Route[];
    history: History;
    release?: string;
    requestUserHasAds?: boolean;
}

const baseUrlRe = new RegExp("^" + BASE_URL);

const resolveToLocation = (to: OwnProps["to"], currentLocation: Location) =>
    typeof to === "function" ? to(currentLocation) : to;

const normalizeToLocation = (to: LocationDescriptor, currentLocation: Location) => {
    return typeof to === "string" ? createLocation(to, null, null, currentLocation) : to;
};

class LinkWithRoutes extends React.PureComponent<OwnProps & InjectedProps> {
    public static defaultProps = {
        replace: false,
    };

    public render() {
        const { to, replace, innerRef, routes, history, requestUserHasAds, ...rest } = this.props;

        let href: string;
        if (typeof to === "string") {
            href = to;
        } else {
            const location = normalizeToLocation(
                resolveToLocation(to, history.location),
                history.location,
            );
            href = location ? history.createHref(location) : "";
        }

        // https://trello.com/c/tamkDLUC/6540-do-get-parametrov-pre-login-a-signup-linky-v-headeri-nevkladat-nexturl-ale-pridat-az-na-onclick
        // Ak URL obsahuje query string, skontroluj parameter `next`.
        // Ak ide o login alebo signup URL, odstráni sa query časť.
        const hrefStartsWithSignupOrLogin = href.startsWith(loginUrl) || href.startsWith(signupUrl);
        if (href.includes("?")) {
            const qs = parse(href.substr(href.indexOf("?")));
            if (qs.next && hrefStartsWithSignupOrLogin) {
                href = href.startsWith(loginUrl) ? loginUrl : signupUrl;
            }
        }

        // Určí, či sa má zobraziť interstitial reklama od Googlu.
        // 1. Ak URL smeruje na login alebo signup, reklama sa nezobrazí ('false').
        // 2. Inak sa použije hodnota z atribútu `data-google-interstitial`.
        // 3. Ak chýba, použije sa `requestUserHasAds` (či má používateľ povolené reklamy).
        // 4. Ak nič z toho nie je dostupné, výsledok bude 'false'.
        const dataGoogleInterstitial = hrefStartsWithSignupOrLogin
            ? "false"
            : ((rest["data-google-interstitial"] || requestUserHasAds) ?? "false");

        return (
            <a
                {...rest}
                onClick={this.onClick}
                href={href}
                ref={innerRef}
                data-google-interstitial={dataGoogleInterstitial}
            />
        );
    }

    private onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        const { to, onClick, target, replace, routes, history, release } = this.props;

        const href = event.currentTarget.href;

        if (onClick) {
            onClick(event);
        }

        if (
            !event.defaultPrevented && // onClick prevent default
            event.button === 0 && // ignore everything but left clicks
            !target && // let browser handle "target=_blank" etc.
            !isModifiedEvent(event) && // ignore clicks with modifier keys
            hasRoute(href, routes) // Check if we have handler for this URL
        ) {
            event.preventDefault();

            const method = replace ? history.replace : history.push;
            let destination = to;
            if (typeof destination === "string") {
                // Remove BASE_URL from absolute links
                destination = destination.replace(baseUrlRe, "");
            }
            const location = normalizeToLocation(
                resolveToLocation(destination, history.location),
                history.location,
            );

            if (IS_PROD && !!release && release !== CURRENT_REVISION) {
                window.location.href = location.pathname;
            } else {
                try {
                    apiAuth.verifier(); // Throws exception if csrf token has changed
                    method(location);
                } catch (e) {
                    window.location.href = location.pathname;
                }
            }
        }
    };
}

export const Link: React.FunctionComponent<OwnProps> = React.memo((props) => {
    const { routes, history } = useContext(RouterContext);
    const context = useContext(ReactReduxContext);
    const state = context?.store?.getState();

    const requestUser = state?.request?.user;
    const requestUserHasAds = requestUser ? hasAds(requestUser) : undefined;

    return (
        <LinkWithRoutes
            {...props}
            routes={routes}
            history={history}
            release={state?.release?.version}
            requestUserHasAds={requestUserHasAds}
        />
    );
});

Link.displayName = "Link";

function isModifiedEvent(event: React.MouseEvent<HTMLAnchorElement>) {
    return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}
